/**
 * Order Restoration Utilities
 * 
 * Shared functions for restoring catering orders (both single and combined)
 * Used by: cateringPastOrderCard.jsx and cateringCalendarView/index.jsx
 */

import { formatDateForInput } from "./common";

/**
 * Handles combined order restoration
 * Processes all packages in a combined order and sets up cart for restoration
 * 
 * @param {Object} params - Parameters object
 * @param {Object} params.orderData - Complete order data from API
 * @param {boolean} params.isModifyMode - Whether this is a modify operation
 * @param {Object} params.modifyModeData - Modify mode configuration (if applicable)
 * @param {boolean} params.isUnpaidMode - Whether this is an unpaid order
 * @param {Object} params.unpaidModeData - Unpaid mode configuration (if applicable)
 * @param {boolean} params.isReorderMode - Whether this is a reorder operation
 * @param {Object} params.reorderModeData - Reorder mode configuration (if applicable)
 * @param {Function} params.dispatch - Redux dispatch function
 * @param {Function} params.navigate - React Router navigate function
 * @param {Function} params.setLoadingState - Function to update loading state
 * @param {Object} params.actions - Redux actions object containing all needed actions
 * @returns {Promise<void>}
 */
export const handleCombinedOrderRestoration = async ({
  orderData,
  isModifyMode = false,
  modifyModeData = null,
  isUnpaidMode = false,
  unpaidModeData = null,
  isReorderMode = false,
  reorderModeData = null,
  dispatch,
  navigate,
  setLoadingState,
  actions,
  fullRestoration = false, // If true, fetches items; if false, only packages
}) => {
  try {
    setLoadingState(true);
    
    console.log('🎯 ============================================');
    console.log('🎯 Starting COMBINED order restoration');
    console.log('🎯 Order #:', orderData.order_no);
    console.log('🎯 Number of packages:', orderData.combine_orders?.length || 0);
    console.log('🎯 Full restoration:', fullRestoration);
    console.log('🎯 ============================================');
    
    // Set mode flags if needed
    if (isModifyMode && modifyModeData) {
      console.log('🔒 Setting modify mode flags for combined order', modifyModeData);
      dispatch(actions.setModifyOrderMode({
        isModifyOrderMode: true,
        modifyOrderPax: modifyModeData.modifyOrderPax,
        modifyOrderId: modifyModeData.modifyOrderId,
        modifyOrderOriginalTotal: modifyModeData.modifyOrderOriginalTotal,
        combineOrderId: modifyModeData.combineOrderId || null
      }));
    }
    
    if (isUnpaidMode && unpaidModeData) {
      console.log('💳 Setting unpaid mode flags for combined order');
      dispatch(actions.setUnpaidOrderMode({
        isUnpaidOrderMode: true,
        unpaidOrderId: unpaidModeData.unpaidOrderId,
        combineOrderId: unpaidModeData.combineOrderId || null
      }));
    }
    
    if (isReorderMode && reorderModeData) {
      console.log('🔄 Setting reorder mode flags for combined order');
      dispatch(actions.setReorderMode({
        isReorderMode: true,
        reorderOrderId: reorderModeData.reorderOrderId,
        reorderOriginalTotal: reorderModeData.reorderOriginalTotal
      }));
    }
    
    // Clear cart
    console.log('🧹 Clearing cart for combined order restoration');
    dispatch(actions.clearCateringItemsToRestore());
    dispatch(actions.clearCateringCartItems());
    dispatch(actions.setCateringCartFilters({ pax: 20, date: "", time: "" }));
    
    // Set address from past order if available
    if (orderData.address_id) {
      dispatch(actions.setPastOrderAddressId(parseInt(orderData.address_id)));
      console.log('Stored past order address ID:', orderData.address_id);
    }
    
    // Process each package in the combined order
    const combineOrders = orderData.combine_orders || [];
    if (!combineOrders.length) {
      console.error('No combine_orders found in order data');
      setLoadingState(false);
      return { success: false, error: 'No packages found' };
    }
    
    console.log(`📦 Processing ${combineOrders.length} packages in combined order`);
    
    const restoredPackages = [];
    
    // First, fetch the main package to get additional_packages list
    const mainPackageOrder = combineOrders[0];
    const mainRestaurantId = mainPackageOrder.restaurant_id || orderData.restaurant_id;
    const mainPackageId = mainPackageOrder.service_item_id;
    
    const mainPackageDetail = await dispatch(actions.getCateringPackageDetailAsync(mainRestaurantId, mainPackageId, "", "en", 3, true));
    const additionalPackagesList = mainPackageDetail?.additional_packages || [];
    
    // Process each package sequentially to fetch its data and restore items
    for (let i = 0; i < combineOrders.length; i++) {
      const pkgOrder = combineOrders[i];
      // For additional services, restaurant_id might be empty - use main package's restaurant_id
      const pkgRestaurantId = pkgOrder.restaurant_id || combineOrders[0].restaurant_id || orderData.restaurant_id;
      const pkgPackageId = pkgOrder.service_item_id;
      const pkgPax = parseInt(pkgOrder.no_of_pax) || 20;
      
      try {
        let fullPackageData;
        
        if (i === 0) {
          // Main package - fetch from packages list
          const fetchedPackages = await dispatch(actions.getCateringPackagesAsync(pkgRestaurantId, "", "en", 3));
          const packagesData = fetchedPackages?.payload || fetchedPackages;
          fullPackageData = packagesData?.find?.(pkg => pkg.id === pkgPackageId);
          
          if (!fullPackageData) {
            console.error(`Could not find main package ${pkgPackageId} in restaurant ${pkgRestaurantId}`);
            continue;
          }
        } else {
          // Additional service - get from additional_packages list
          fullPackageData = additionalPackagesList?.find?.(pkg => pkg.id === pkgPackageId);
          
          if (!fullPackageData) {
            console.error(`Could not find additional service package ${pkgPackageId} in additional_packages list`);
            continue;
          }
        }
        
        let completePackage;
        
        if (fullRestoration) {
          // Full restoration - fetch items and reconstruct them
          completePackage = await buildPackageWithItems({
            pkgOrder,
            fullPackageData,
            pkgRestaurantId,
            pkgPackageId,
            pkgPax,
            i,
            dispatch,
            actions
          });
          
          if (!completePackage) {
            console.error(`Failed to build package ${pkgPackageId} with items`);
            continue; // Skip this package and continue with others
          }
        } else {
          // Simplified restoration - just package structure
          completePackage = {
            ...fullPackageData,
            id: pkgPackageId,
            restaurant_id: pkgRestaurantId,
            pax: pkgPax,
            isAdditionalService: i > 0, // Flag for additional service packages
            service_item_date: pkgOrder.service_item_date,
            service_item_slot: pkgOrder.service_item_slot,
            originalOrderId: pkgOrder.id || pkgOrder.catering_order_id,
          };
        }
        
        restoredPackages.push(completePackage);
        
      } catch (error) {
        console.error(`Error processing package ${i + 1}:`, error);
        // Continue with other packages even if one fails
      }
    }
    
    
    if (restoredPackages.length === 0) {
      console.error('Failed to prepare any packages from combined order');
      setLoadingState(false);
      return { success: false, error: 'Failed to prepare packages' };
    }
    
    // Set filters using the first package's date/time
    const formattedDate = formatDateForInput(restoredPackages[0].service_item_date || "");
    dispatch(actions.setCateringCartFilters({
      pax: restoredPackages[0].pax || 20,
      date: formattedDate,
      time: restoredPackages[0].service_item_slot || ""
    }));
    
    // Restore delivery charge from order data
    const deliveryCharge = parseFloat(orderData.delivery_charge || 0);
    if (deliveryCharge > 0) {
      dispatch(actions.setCateringDeliveryCharge(deliveryCharge));
    }
    
    // Add all packages to cart
    if (fullRestoration) {
      // For full restoration, add packages with items
      restoredPackages.forEach(pkg => {
        // Step 1: Add the package structure (this strips items)
        dispatch(actions.addCateringCartPackage(pkg));
        
        // Step 2: Add each item to the package
        if (pkg.items && Array.isArray(pkg.items) && pkg.items.length > 0) {
          pkg.items.forEach(item => {
            dispatch(actions.addItemToCateringPackage({
              packageId: pkg.id,
              item: item,
              packageMeta: {
                title: pkg.name_en || pkg.title,
                basePrice: pkg.basePrice || 0
              }
            }));
          });
        }
      });
    } else {
      // For simplified restoration, just add package structures
      restoredPackages.forEach(pkg => {
        dispatch(actions.addCateringCartPackage(pkg));
      });
    }
    
    // Set flag to hide Book Slot button for pending payment orders
    if (!isModifyMode && !isReorderMode) {
      dispatch(actions.setIsPastOrderRestoration(true));
    }
    
    // Navigate to the first package's detail page
    const firstPackageId = restoredPackages[0].id;
    
    setLoadingState(false);
    navigate(`/catering-package/${firstPackageId}`);
    
    return { success: true, packages: restoredPackages };
    
  } catch (error) {
    console.error('❌ Error restoring combined order:', error);
    setLoadingState(false);
    return { success: false, error: error.message };
  }
};

/**
 * Builds package with items (full restoration)
 * Fetches package details, categories, and reconstructs all items with proper addOrderHistory
 */
const buildPackageWithItems = async ({
  pkgOrder,
  fullPackageData,
  pkgRestaurantId,
  pkgPackageId,
  pkgPax,
  i,
  dispatch,
  actions
}) => {
  try {
    // Fetch categories for this package
    const categoriesResponse = await dispatch(actions.getRestaurantItemsCategoriesAsync(pkgRestaurantId, "en", pkgPackageId, true));
    
    if (!categoriesResponse || !Array.isArray(categoriesResponse)) {
      console.error(`Failed to fetch categories for package ${pkgPackageId}`);
      return null;
    }
    
    // Fetch package detail to get items
    const detailResponse = await dispatch(actions.getCateringPackageDetailAsync(pkgRestaurantId, pkgPackageId, "", "en", 3, true));
    
    if (!detailResponse || !detailResponse.categories) {
      console.error(`Failed to fetch package detail for package ${pkgPackageId}`);
      return null;
    }
    
    // Extract all items from categories
    const allItems = detailResponse.categories.flatMap(category => 
      (category.catering_service_item || []).map(item => ({
        ...item,
        category_id: category.id,
        category_name_en: category.name_en,
        category_name_ar: category.name_ar
      }))
    );
    
    // Prepare items to restore from past order
    const pkgServiceItems = pkgOrder.service_item || [];
    
    // Group items by menu_id, category_id, and add_on_type
    const itemsMap = {};
    pkgServiceItems.forEach(serviceItem => {
      const menuId = serviceItem.menu_item.menu_id;
      const categoryId = serviceItem.category_id;
      const addOnType = serviceItem.add_on_type || "";
      const key = `${menuId}_${categoryId}_${addOnType}`;
      
      if (!itemsMap[key]) {
        itemsMap[key] = {
          menu_id: menuId,
          category_id: categoryId,
          quantity: 0,
          add_on_price: 0,
          add_on_type: addOnType,
          complimentaryCount: 0
        };
      }
      
      const itemQuantity = parseInt(serviceItem.quantity) || parseInt(serviceItem.choose) || 1;
      const isComplimentary = serviceItem.is_complementary === true || serviceItem.is_complementary === 1;
      
      itemsMap[key].quantity += itemQuantity;
      
      if (!isComplimentary) {
        itemsMap[key].add_on_price += parseFloat(serviceItem.add_on_price || 0);
      }
      
      if (isComplimentary) {
        itemsMap[key].complimentaryCount += itemQuantity;
      }
    });
    
    const itemsToRestore = Object.values(itemsMap);
    
    // Reconstruct items with addOrderHistory
    const restoredItems = [];
    
    // Find category limits for this package
    const packageCategories = fullPackageData.categories || detailResponse.categories || [];
    
    // Group items by category for proper addOrderHistory calculation
    const itemsByCategory = {};
    itemsToRestore.forEach(itemToRestore => {
      const categoryId = itemToRestore.category_id;
      if (!itemsByCategory[categoryId]) {
        itemsByCategory[categoryId] = [];
      }
      itemsByCategory[categoryId].push(itemToRestore);
    });
    
    // Process each category
    Object.keys(itemsByCategory).sort((a, b) => parseInt(a) - parseInt(b)).forEach(categoryId => {
      const categoryItems = itemsByCategory[categoryId];
      
      // Find category limit
      const categoryLimit = packageCategories.find(cat => cat.id === parseInt(categoryId));
      let maxFreeItems = 0;
      
      if (categoryLimit && categoryLimit.choose_rule && categoryLimit.choose_rule.length > 0) {
        const sortedRules = [...categoryLimit.choose_rule].sort((a, b) => parseInt(b.pax) - parseInt(a.pax));
        const matchingRule = sortedRules.find(rule => pkgPax >= parseInt(rule.pax));
        
        if (matchingRule && matchingRule.choose > 0) {
          maxFreeItems = matchingRule.choose;
        }
      }
      
      // Build metadata for all item instances
      const itemMetadata = [];
      categoryItems.forEach((itemToRestore) => {
        const fullItem = allItems.find(item => item.id === itemToRestore.menu_id);
        
        if (fullItem) {
          const addOnType = itemToRestore.add_on_type || "";
          
          // Calculate unit price
          let unitPrice = 0;
          if (fullItem.add_on_options && Array.isArray(fullItem.add_on_options) && fullItem.add_on_options.length > 0) {
            if (fullItem.add_on_options[0].add_on_type !== undefined) {
              if (addOnType) {
                const matchingOption = fullItem.add_on_options.find(option => option.add_on_type === addOnType);
                unitPrice = matchingOption && matchingOption.price ? parseFloat(matchingOption.price) : 0;
              }
            } else {
              const matchingOption = fullItem.add_on_options.find(option => {
                const paxFrom = parseInt(option.pax_from || 0);
                const paxTo = parseInt(option.pax_to || 999);
                return pkgPax >= paxFrom && pkgPax <= paxTo;
              });
              unitPrice = matchingOption && matchingOption.price ? parseFloat(matchingOption.price) : 0;
            }
          }
          
          // Determine chargeable vs free instances
          const complimentaryCount = itemToRestore.complimentaryCount || 0;
          const addOnPrice = itemToRestore.add_on_price || 0;
          let chargeableForThisItem = 0;
          
          if (complimentaryCount > 0) {
            if (unitPrice > 0 && addOnPrice > 0) {
              const chargeableFromPrice = Math.round(addOnPrice / unitPrice);
              chargeableForThisItem = Math.max(0, chargeableFromPrice - complimentaryCount);
            } else {
              chargeableForThisItem = 0;
            }
          } else if (unitPrice > 0 && addOnPrice > 0) {
            chargeableForThisItem = Math.round(addOnPrice / unitPrice);
          }
          
          const freeForThisItem = itemToRestore.quantity - chargeableForThisItem;
          
          itemMetadata.push({
            itemId: itemToRestore.menu_id,
            itemName: fullItem.name_en,
            addOnType: addOnType,
            totalQuantity: itemToRestore.quantity,
            freeInstances: freeForThisItem,
            chargeableInstances: chargeableForThisItem,
            unitPrice: unitPrice,
            complimentaryCount: complimentaryCount
          });
        }
      });
      
      // Build allInstances array with proper ordering
      const allInstances = [];
      let globalInstanceCounter = 0;
      
      // Add all free instances first
      itemMetadata.forEach(meta => {
        for (let i = 0; i < meta.freeInstances; i++) {
          allInstances.push({
            itemId: meta.itemId,
            itemName: meta.itemName,
            addOnType: meta.addOnType,
            isChargeable: false,
            globalOrder: globalInstanceCounter++,
            unitPrice: meta.unitPrice
          });
        }
      });
      
      // Then add all chargeable instances
      itemMetadata.forEach(meta => {
        for (let i = 0; i < meta.chargeableInstances; i++) {
          allInstances.push({
            itemId: meta.itemId,
            itemName: meta.itemName,
            addOnType: meta.addOnType,
            isChargeable: true,
            globalOrder: globalInstanceCounter++,
            unitPrice: meta.unitPrice
          });
        }
      });
      
      // Now rebuild items with correct addOrderHistory
      categoryItems.forEach((itemToRestore) => {
        const fullItem = allItems.find(item => item.id === itemToRestore.menu_id);
        
        if (fullItem) {
          const addOnType = itemToRestore.add_on_type || "";
          
          // Find all instances of this item AND add_on_type in the global order
          const itemInstances = allInstances.filter(inst => 
            inst.itemId === itemToRestore.menu_id && 
            (inst.addOnType || "") === addOnType
          );
          const addOrderHistory = itemInstances.map(inst => inst.globalOrder);
          
          // Create the item object with reconstructed addOrderHistory
          const itemToAdd = {
            ...fullItem,
            quantity: itemToRestore.quantity,
            category_id: itemToRestore.category_id || fullItem.category_id,
            addOrderHistory: addOrderHistory
          };
          
          // Set complimentaryCount from itemToRestore
          const itemMeta = itemMetadata.find(meta => 
            meta.itemId === itemToRestore.menu_id && 
            (meta.addOnType || "") === addOnType
          );
          if (itemMeta && itemMeta.complimentaryCount > 0) {
            itemToAdd.complimentaryCount = itemMeta.complimentaryCount;
          } else if (itemToRestore.complimentaryCount > 0) {
            itemToAdd.complimentaryCount = itemToRestore.complimentaryCount;
          }
          
          // Include add_on_type if present
          if (addOnType) {
            itemToAdd.add_on_type = addOnType;
            if (fullItem.add_on_options && Array.isArray(fullItem.add_on_options) && fullItem.add_on_options.length > 0) {
              if (fullItem.add_on_options[0].add_on_type !== undefined) {
                const matchingOption = fullItem.add_on_options.find(option => option.add_on_type === addOnType);
                if (matchingOption && matchingOption.price) {
                  itemToAdd.price = `KWD ${parseFloat(matchingOption.price).toFixed(3)}`;
                }
              }
            }
          }
          
          restoredItems.push(itemToAdd);
        }
      });
    });
    
    // Create complete package with restored items
    // IMPORTANT: Merge detailResponse AFTER fullPackageData to ensure pricing_tiers is included
    const completePackage = {
      ...fullPackageData, // Base package data from list
      ...detailResponse, // Full details including pricing_tiers, categories, etc.
      id: pkgPackageId, // Preserve correct ID
      restaurant_id: pkgRestaurantId, // Preserve restaurant ID
      pax: pkgPax, // Set PAX from order
      items: restoredItems, // Use restored items
      isAdditionalService: i > 0, // Flag for additional service packages
      service_item_date: pkgOrder.service_item_date,
      service_item_slot: pkgOrder.service_item_slot,
      originalOrderId: pkgOrder.id || pkgOrder.catering_order_id,
      additional_packages: i === 0 ? (detailResponse.additional_packages || []) : undefined
    };
    
    return completePackage;
    
  } catch (error) {
    console.error(`Error building package with items:`, error);
    return null;
  }
};

/**
 * Handles single package order restoration
 * 
 * @param {Object} params - Parameters object
 * @param {Object} params.orderData - Complete order data from API
 * @param {boolean} params.isModifyMode - Whether this is a modify operation
 * @param {Object} params.modifyModeData - Modify mode configuration (if applicable)
 * @param {boolean} params.isUnpaidMode - Whether this is an unpaid order
 * @param {Object} params.unpaidModeData - Unpaid mode configuration (if applicable)
 * @param {boolean} params.isReorderMode - Whether this is a reorder operation
 * @param {Object} params.reorderModeData - Reorder mode configuration (if applicable)
 * @param {Function} params.dispatch - Redux dispatch function
 * @param {Function} params.navigate - React Router navigate function
 * @param {Function} params.setLoadingState - Function to update loading state
 * @param {Object} params.actions - Redux actions object containing all needed actions
 * @returns {Promise<void>}
 */
export const handleSingleOrderRestoration = async ({
  orderData,
  isModifyMode = false,
  modifyModeData = null,
  isUnpaidMode = false,
  unpaidModeData = null,
  isReorderMode = false,
  reorderModeData = null,
  dispatch,
  navigate,
  setLoadingState,
  actions,
  fullRestoration = false,
}) => {
  try {
    console.log('🚀 ============================================');
    console.log('🚀 Starting single order restoration');
    console.log('🚀 Order #:', orderData.order_no);
    console.log('🚀 Is Modify Mode:', isModifyMode);
    console.log('🚀 Full Restoration:', fullRestoration);
    console.log('🚀 ============================================');
    
    // Set mode flags FIRST before clearing cart items
    if (isModifyMode && modifyModeData) {
      console.log('🔒 Setting modify mode flags in Redux');
      dispatch(actions.setModifyOrderMode({
        isModifyOrderMode: true,
        modifyOrderPax: modifyModeData.modifyOrderPax,
        modifyOrderId: modifyModeData.modifyOrderId,
        modifyOrderOriginalTotal: modifyModeData.modifyOrderOriginalTotal
      }));
    }
    
    if (isUnpaidMode && unpaidModeData) {
      console.log('💳 Setting unpaid mode flags in Redux');
      dispatch(actions.setUnpaidOrderMode({
        isUnpaidOrderMode: true,
        unpaidOrderId: unpaidModeData.unpaidOrderId,
        combineOrderId: unpaidModeData.combineOrderId || null
      }));
    }
    
    if (isReorderMode && reorderModeData) {
      console.log('🔄 Setting reorder mode flags in Redux');
      dispatch(actions.setReorderMode({
        isReorderMode: true,
        reorderOrderId: reorderModeData.reorderOrderId,
        reorderOriginalTotal: reorderModeData.reorderOriginalTotal
      }));
    }
    
    // Clear cart items but preserve all mode flags
    console.log('🧹 Clearing itemsToRestore from Redux');
    dispatch(actions.clearCateringItemsToRestore());
    
    console.log('🧹 Clearing cart items ONLY (preserves all mode flags)');
    dispatch(actions.clearCateringCartItems());
    dispatch(actions.setCateringCartFilters({
      pax: 20,
      date: "",
      time: ""
    }));
    
    // Fetch catering packages to populate Redux
    const targetRestaurantId = orderData.restaurant_id;
    const targetPackageId = orderData.service_item_id;
    const targetPax = parseInt(orderData.no_of_pax) || 20;
    
    console.log('Fetching catering packages for restaurant:', targetRestaurantId);
    const fetchedPackages = await dispatch(actions.getCateringPackagesAsync(targetRestaurantId, "", "en", 3));
    
    const packagesData = fetchedPackages?.payload || fetchedPackages;
    const fullPackageData = packagesData?.find?.(pkg => pkg.id === targetPackageId);
    
    if (!fullPackageData) {
      console.error('Could not find package data:', targetPackageId);
      setLoadingState(false);
      return { success: false, error: 'Package not found' };
    }
    
    console.log('Full package data retrieved:', fullPackageData);
    
    // Set restaurant ID
    dispatch(actions.setCateringRestaurantId(targetRestaurantId));
    
    // Set address from past order if available
    if (orderData.address_id) {
      dispatch(actions.setPastOrderAddressId(parseInt(orderData.address_id)));
      console.log('Stored past order address ID:', orderData.address_id);
    }
    
    // Set filters based on order data
    const filters = {
      pax: targetPax,
      date: formatDateForInput(orderData.service_item_date),
      time: orderData.service_item_slot
    };
    console.log('Setting filters from order details:', filters);
    dispatch(actions.setCateringCartFilters(filters));
    
    // Restore delivery charge
    const deliveryCharge = parseFloat(orderData.delivery_charge || 0);
    if (deliveryCharge > 0) {
      dispatch(actions.setCateringDeliveryCharge(deliveryCharge));
      console.log('💰 Restored delivery charge:', deliveryCharge);
    }
    
    if (fullRestoration) {
      // Full restoration - use buildPackageWithItems to reconstruct everything
      console.log('🔄 Full restoration mode - building package with items');
      
      const completePackage = await buildPackageWithItems({
        pkgOrder: orderData,
        fullPackageData,
        pkgRestaurantId: targetRestaurantId,
        pkgPackageId: targetPackageId,
        pkgPax: targetPax,
        i: 0,
        dispatch,
        actions
      });
      
      if (!completePackage) {
        console.error('Failed to build package with items');
        setLoadingState(false);
        return { success: false, error: 'Failed to build package with items' };
      }
      
      console.log('✅ Package built with items:', completePackage);
      
      // Add package structure (this strips items)
      dispatch(actions.addCateringCartPackage(completePackage));
      
      // Add each item to the package
      if (completePackage.items && Array.isArray(completePackage.items) && completePackage.items.length > 0) {
        console.log(`📦 Adding ${completePackage.items.length} items to package`);
        completePackage.items.forEach(item => {
          console.log(`  ➕ Adding item: ${item.name_en} (qty: ${item.quantity})`);
          dispatch(actions.addItemToCateringPackage({
            packageId: targetPackageId,
            item: item,
            packageMeta: {
              title: completePackage.name_en || completePackage.title,
              basePrice: completePackage.basePrice || 0
            }
          }));
        });
      }
    } else {
      // Simplified restoration - just store items as metadata
      console.log('📝 Simplified restoration mode - storing items as metadata');
    
    // Add the FULL package data to cart
    console.log('Adding FULL package structure to cart');
    dispatch(actions.addCateringCartPackage(fullPackageData));
    
    // Prepare service items data for restoration
    const detailedServiceItems = orderData.service_item || [];
    
    // Group items by menu_id, category_id, and add_on_type
    const itemsMap = {};
    detailedServiceItems.forEach(serviceItem => {
      const menuId = serviceItem.menu_item.menu_id;
      const categoryId = serviceItem.category_id;
      const addOnType = serviceItem.add_on_type || "";
      const key = `${menuId}_${categoryId}_${addOnType}`;
      
      if (!itemsMap[key]) {
        itemsMap[key] = {
          menu_id: menuId,
          category_id: categoryId,
          quantity: 0,
          add_on_price: 0,
          add_on_type: addOnType,
          complimentaryCount: 0
        };
      }
      
      const itemQuantity = parseInt(serviceItem.quantity) || parseInt(serviceItem.choose) || 1;
      const isComplimentary = serviceItem.is_complementary === true || serviceItem.is_complementary === 1;
      
      itemsMap[key].quantity += itemQuantity;
      
      if (!isComplimentary) {
        itemsMap[key].add_on_price += parseFloat(serviceItem.add_on_price || 0);
      }
      
      if (isComplimentary) {
        itemsMap[key].complimentaryCount += itemQuantity;
      }
    });
    
    const itemsToRestore = Object.values(itemsMap);
    
    console.log('📋 Prepared items to restore:', {
      originalCount: detailedServiceItems.length,
      groupedCount: itemsToRestore.length
    });
    
    // Store items to restore in Redux
    dispatch(actions.setCateringItemsToRestore(itemsToRestore));
    }
    
    // Set flag to hide Book Slot button for pending payment orders
    if (!isModifyMode && !isReorderMode) {
      dispatch(actions.setIsPastOrderRestoration(true));
    }
    
    console.log('Navigating to package detail:', targetPackageId);
    
    setLoadingState(false);
    navigate(`/catering-package/${targetPackageId}`);
    
    return { success: true };
    
  } catch (error) {
    console.error('Error restoring single order:', error);
    setLoadingState(false);
    return { success: false, error: error.message };
  }
};

/**
 * Main entry point for order restoration
 * Determines if order is combined or single and calls appropriate handler
 * 
 * @param {Object} params - All parameters needed for restoration
 * @returns {Promise<Object>} - Result object with success status
 */
export const restoreOrder = async (params) => {
  const { orderData } = params;
  
  // Check if this is a combined order
  const isCombinedOrder = orderData.is_combine_order === "yes" || orderData.is_combined_order === "yes";
  
  if (isCombinedOrder) {
    console.log('🎯 Detected COMBINED order - using combined restoration');
    return await handleCombinedOrderRestoration(params);
  } else {
    console.log('🚀 Detected SINGLE order - using single restoration');
    return await handleSingleOrderRestoration(params);
  }
};
